import { BarrasResultado, Barras, FilaResultado } from '../components/Barras';
import { PedidoLista } from '../components/ListaDetalle';
import { dinero, entero, porcentaje, RAMPA } from '../lib/graficas';

/**
 * Analítica → PARA VENDER Y ANUNCIAR y EMBUDO (0.46.0). Conclusiones en palabras que salen de los datos (cada una con su
 * muestra y su cuidado), y el embudo contactos → agendaron → vinieron → volvieron → pagaron, total, por canal y por
 * asesoría. Cada barra se abre (ListaDetalle) y enseña quiénes son.
 */
export interface Insight { clave: string; pregunta: string; como: string; conclusion: string; muestra: number; minimo: number; cuidado: string; filas: FilaResultado[] }
export interface InsightsResp {
  universo: { citas: number; desde: string | null; hasta: string | null; sinCierre: number; sinEstado: number };
  insights: Insight[];
  dinero: { pregunta: string; suficiente: boolean; conclusion: string; tickets: number; personas: number; desde: string | null; cuidado: string; filas: { nombre: string; personas: number; tickets: number; ingreso: number; ltv: number }[] };
  nota: string;
}
interface FilaEmbudo { nombre: string; contactos: number; agendaron: number; vinieron: number; volvieron: number; pagaron: number }
export interface EmbudoResp {
  pasos: { clave: string; nombre: string; n: number; pctInicio: number; pctAnterior: number }[];
  porCanal: FilaEmbudo[]; porAsesoria: FilaEmbudo[];
  tiempo: { mediana: number | null; n: number; semana: number };
  nota: string;
}

const CSS = `
.iv-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 430px), 1fr)); gap: 14px; }
.iv-card { padding: 16px; display: grid; gap: 10px; align-content: start; min-width: 0; }
.iv-card h3 { margin: 0; font-size: 15.5px; font-weight: 600; color: var(--gold-dark); }
.iv-concl { margin: 0; font-size: 15px; line-height: 1.5; color: var(--ink); }
.iv-como { font-size: 12px; color: var(--ink-soft); line-height: 1.45; }
.iv-card details { font-size: 12px; color: var(--ink-soft); line-height: 1.5; }
.iv-card details summary { cursor: pointer; color: var(--gold-dark); font-weight: 600; }
.iv-aviso { padding: 10px 14px; font-size: 13px; line-height: 1.5; background: var(--warning-bg); color: var(--warning); border: none; }
.iv-tabla { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 520px; }
.iv-tabla th { text-align: right; padding: 6px 8px; font-size: 11.5px; color: var(--ink-soft); font-weight: 600; white-space: nowrap; }
.iv-tabla th:first-child, .iv-tabla td:first-child { text-align: left; }
.iv-tabla td { padding: 4px 6px; border-top: 1px solid var(--border); text-align: right; white-space: nowrap; }
.iv-celda { border: none; background: transparent; box-shadow: none; padding: 4px 6px; border-radius: 8px; font: inherit; color: var(--ink); cursor: pointer; text-align: right; width: 100%; font-variant-numeric: tabular-nums; }
.iv-celda:hover { background: var(--bg-alt); transform: none; }
.iv-celda:focus-visible { outline: 2px solid var(--gold); }
.iv-celda small { display: block; color: var(--ink-soft); font-size: 11px; }
.iv-mini { display: inline-block; vertical-align: middle; width: 54px; height: 8px; background: rgba(0,0,0,.06); border-radius: 4px; margin-left: 6px; overflow: hidden; }
.iv-mini i { display: block; height: 100%; background: #12936b; border-radius: 4px; }
`;

export function TabVender({ d, onLista }: { d: InsightsResp; onLista: (p: PedidoLista) => void }) {
  const u = d.universo;
  const fecha = (x: string | null) => (x ? new Date(x + 'T12:00:00').toLocaleDateString('es-MX', { month: 'short', year: 'numeric' }) : '');
  return (
    <div style={{ display: 'grid', gap: 14 }} data-tab-vender>
      <style>{CSS}</style>
      <div className="muted" style={{ fontSize: 13, lineHeight: 1.5, padding: '0 2px' }}>
        Conclusiones que salen de los datos de la clínica, cada una con sobre cuántos casos se calculó y qué la puede sesgar.
        Usan <strong>todo el historial</strong> ({entero(u.citas)} citas con resultado{u.desde ? `, de ${fecha(u.desde)} a ${fecha(u.hasta)}` : ''}): no cambian con el periodo de arriba.
        Toca una barra para ver las citas detrás.
      </div>
      {u.sinCierre > 0 && (
        <div className="card iv-aviso" data-aviso-sin-cierre>
          <strong>{entero(u.sinCierre)} citas del sitio y de Halory ya pasaron y nadie marcó si la persona llegó.</strong> No entran en estas conclusiones
          (no se sabe). Cerrarlas en recepción (llegó, canceló o no llegó) es lo que más mejora estas cuentas.
        </div>
      )}
      <div className="iv-grid">
        {d.insights.map((ins) => (
          <div key={ins.clave} className="card iv-card" data-insight={ins.clave}>
            <h3>{ins.pregunta}</h3>
            <p className="iv-concl" data-conclusion>{ins.conclusion}</p>
            {ins.filas.length ? (
              <BarrasResultado filas={ins.filas} datos={ins.clave}
                onAbrir={(k) => { const f = ins.filas.find((x) => x.clave === k)!; onLista({ titulo: `${ins.pregunta.replace(/^¿|\?$/g, '')}: ${f.nombre}`, subtitulo: `${f.llego} llegaron, ${f.cancelo} cancelaron y ${f.noLlego} no llegaron.`, qs: { tipo: 'citas', insight: ins.clave, valor: k } }); }} />
            ) : <p className="muted" style={{ fontSize: 13 }}>Todavía no hay citas con resultado.</p>}
            <div className="iv-como">{ins.como}</div>
            <details><summary>Cuidado al leerla · {entero(ins.muestra)} citas</summary>{ins.cuidado} Una barra con menos de {ins.minimo} citas se enseña más clara y no entra en la conclusión.</details>
          </div>
        ))}
        <div className="card iv-card" data-insight="dinero">
          <h3>{d.dinero.pregunta}</h3>
          <p className="iv-concl" data-conclusion>{d.dinero.conclusion}</p>
          {d.dinero.filas.length > 0 && (
            <Barras filas={d.dinero.filas.map((f) => ({ clave: f.nombre, nombre: f.nombre, n: f.ingreso, extra: `· ${entero(f.personas)} ${f.personas === 1 ? 'persona' : 'personas'} · ${dinero(f.ltv)} c/u` }))}
              formato={(f) => dinero(f.n)} datos="dinero-canal" />
          )}
          <details><summary>Cuidado al leerla · {entero(d.dinero.tickets)} tickets</summary>{d.dinero.cuidado}</details>
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: 0 }}>{d.nota}</p>
    </div>
  );
}

const PASOS_TXT: Record<string, string> = { contactos: 'Contactos', agendaron: 'Agendaron', vinieron: 'Vinieron', volvieron: 'Volvieron', pagaron: 'Pagaron' };

export function TabEmbudo({ e, periodoTxt, asesoria, onLista }: { e: EmbudoResp; periodoTxt: string; asesoria: string; onLista: (p: PedidoLista) => void }) {
  const base: Record<string, string> = { ...(asesoria ? { asesora: asesoria } : {}) };
  const abrir = (paso: string, por?: 'canal' | 'asesoria', valor?: string) => onLista({
    titulo: `${PASOS_TXT[paso]}${valor ? ` · ${valor}` : ''}`,
    subtitulo: `De quienes llegaron ${periodoTxt}.`,
    qs: { tipo: 'embudo', paso, ...(por && valor ? { por, valor } : {}), ...base },
  });
  const c = e.pasos[0]?.n || 0;
  return (
    <div style={{ display: 'grid', gap: 14 }} data-tab-embudo>
      <style>{CSS}</style>
      <div className="card iv-card">
        <h3>¿Cuántos de los que llegan terminan viniendo y pagando?</h3>
        <div className="iv-como">De quienes se dieron de alta {periodoTxt}: cuántos agendaron, cuántos vinieron, cuántos volvieron y cuántos pagaron en la clínica. Cada paso dice su % de los contactos y, en gris, su % del paso anterior. Toca un paso para ver a las personas.</div>
        <Barras filas={e.pasos.map((p, i) => ({ clave: p.clave, nombre: p.nombre, n: p.n, color: RAMPA[Math.min(5, i + 1)], extra: i ? `· ${p.pctAnterior} % del anterior` : undefined }))} total={c} datos="embudo"
          onAbrir={(k) => abrir(k)} />
        {e.tiempo.n > 0 && (
          <div style={{ fontSize: 13 }} data-tiempo>
            <strong>Cuánto tardan en venir la primera vez:</strong> la mitad viene en {e.tiempo.mediana} {e.tiempo.mediana === 1 ? 'día' : 'días'} o menos desde que llegó
            ({entero(e.tiempo.semana)} de {entero(e.tiempo.n)} vinieron en la primera semana, {porcentaje(e.tiempo.semana, e.tiempo.n)}).
            {e.tiempo.n < 20 ? ' Son pocos casos todavía.' : ''}
          </div>
        )}
        <div className="iv-como">{e.nota}</div>
      </div>
      <TablaEmbudo titulo="¿Qué canal trae gente que viene?" como="Por el canal por el que llegó cada persona. Debajo de cada número, el % de los contactos de ese canal; la barrita es cuántos vinieron. Toca un número para ver a las personas."
        filas={e.porCanal} onAbrir={(paso, v) => abrir(paso, 'canal', v)} datos="canal" />
      <TablaEmbudo titulo="¿Cómo le va a la cartera de cada asesoría?" como="Por la asesoría a la que está asignada cada persona (la que dice Halory). «Sin asignar» es quien no tiene a nadie."
        filas={e.porAsesoria} onAbrir={(paso, v) => abrir(paso, 'asesoria', v)} datos="asesoria" />
    </div>
  );
}

function TablaEmbudo({ titulo, como, filas, onAbrir, datos }: { titulo: string; como: string; filas: FilaEmbudo[]; onAbrir: (paso: string, valor: string) => void; datos: string }) {
  const pasos = ['contactos', 'agendaron', 'vinieron', 'volvieron', 'pagaron'] as const;
  return (
    <div className="card iv-card" data-tabla-embudo={datos}>
      <h3>{titulo}</h3>
      <div className="iv-como">{como}</div>
      {filas.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>Nadie llegó en este periodo.</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="iv-tabla">
            <thead><tr><th>{datos === 'canal' ? 'Canal' : 'Asesoría'}</th>{pasos.map((p) => <th key={p}>{PASOS_TXT[p]}</th>)}</tr></thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.nombre} data-fila={f.nombre}>
                  <td style={{ fontWeight: 600, whiteSpace: 'normal', minWidth: 110 }}>{f.nombre}</td>
                  {pasos.map((p) => (
                    <td key={p}>
                      <button type="button" className="iv-celda" onClick={() => onAbrir(p, f.nombre)} disabled={!f[p]} aria-label={`${PASOS_TXT[p]} de ${f.nombre}: ${f[p]}`} data-celda={p}>
                        {entero(f[p])}{p === 'vinieron' && f.contactos ? <span className="iv-mini" aria-hidden="true"><i style={{ width: `${(f.vinieron / f.contactos) * 100}%` }} /></span> : null}
                        {p !== 'contactos' && <small>{porcentaje(f[p], f.contactos)}</small>}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
